import 'babel-polyfill';
import 'es6-promise/auto';

// Install the legacy call bridge before anything else imports axios or fires a
// request. It replaces axios's default adapter, so any module that captures the
// adapter at import time must see the bridge already in place.
//
// Components still POST `API_Meth` envelopes to the retired backends; the bridge
// turns each into a REST call against pacmny-api and answers in the shape the
// call site expects. See src/api2/legacyBridge.js.
import { installLegacyBridge } from '@/api2/legacyBridge';
import { installVideoBridge } from '@/api2/videoBridge';
import { bootstrapSession, isCallbackUrl } from '@/auth/bootstrapSession';

installLegacyBridge();

// Main's resumable video upload (#334) posts to keyon's video_upload.php from
// an axios instance of its own (src/api/videoUpload.js), which takes the
// default adapter as it stands when the instance is made. Installed here, in
// front of the legacy bridge, so every upload reaches the API's /video-uploads
// routes with their real statuses (src/api2/videoBridge.js).
installVideoBridge();

// Complete an OIDC redirect BEFORE anything mounts.
//
// The exchange cannot live in a component: this app dispatches an FSM message
// at boot which navigates, and that unmounted the callback page mid-exchange —
// the tokens were never stored, the guard saw no session, and sign-in looped.
// Nothing is mounted here, so nothing can interrupt it.
if (isCallbackUrl()) {
  bootstrapSession();
}

import { createApp, nextTick } from 'vue';
import { createPinia } from 'pinia'; // Vue 3 recommends Pinia instead of Vuex
import store from './store';
import router from './router/index';
import { syncPrivilegesAfterSessionChange, clearPrivilegesStore, clearOrganizationStore } from '@/utils/privilegesSessionSync';
import { LoadingPlugin } from 'vue-loading-overlay';
import 'vue-loading-overlay/dist/css/index.css';

import { library } from '@fortawesome/fontawesome-svg-core';
import {  faAddressBook, faAngleDoubleLeft, faAngleDoubleRight, faAngleLeft, faAngleRight, faArrowRight, faBan, faBars, faBell, faBuilding, faCalendarAlt, faCalendarCheck, faCaretDown, faCaretRight, faCheck, faCheckSquare, faClipboardCheck, faCog, faComment, faCommentAlt, faCommentDots, faCrop, faDollarSign, faEnvelope, faEye, faFax, faFile, faFileAlt, faFileMedical, faFileMedicalAlt, faFilePdf, faFileVideo, faFilter, faImages, faHome, faInbox, faMapMarkedAlt, faMapMarkerAlt, faMinusCircle, faNotesMedical, faPen, faPencilAlt, faPhone, faPlaneDeparture, faPlay, faPlusCircle, faPrescriptionBottle, faQuestionCircle, faReply, faSave, faSearch, faSearchPlus, faSortAmountUp, faSpinner, faStar, faThumbtack, faTimes, faTimesCircle, faTrash, faUser, faUserMd, faUserNurse, faUserPlus, faUsers, faChevronUp, faChevronDown, faChevronRight
} from '@fortawesome/free-solid-svg-icons';

import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
// import Calendar from 'v-calendar/lib/components/calendar.umd'
import Calendar from 'v-calendar'
import 'bootstrap/dist/css/bootstrap.css';
import 'bootstrap/dist/css/bootstrap.min.css'
import "bootstrap-icons/font/bootstrap-icons.css"
import "bootstrap/dist/js/bootstrap.bundle.min.js"
import './stylesheets/keyon.scss';
import './stylesheets/style.scss';
import { h as hFn, computed as computedFn, defineComponent } from 'vue';
// Vue 3 compatible text-highlight replacement (vue-text-highlight is Vue 2 only)
const TextHighlight = defineComponent({
  name: 'TextHighlight',
  props: {
    queries: { type: Array, default: () => [] },
    caseSensitive: { type: Boolean, default: false },
  },
  setup(props, { slots }) {
    const parts = computedFn(() => {
      const text = slots.default ? slots.default().map(vnode => typeof vnode.children === 'string' ? vnode.children : '').join('') : '';
      const q = (props.queries || []).filter(s => s && String(s).length > 0).map(s => String(s).replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
      if (!q.length || !text) return [{ text, highlight: false }];
      const regex = new RegExp(`(${q.join('|')})`, props.caseSensitive ? 'g' : 'gi');
      const result = [];
      let last = 0;
      text.replace(regex, (match, _p1, offset) => {
        if (offset > last) result.push({ text: text.slice(last, offset), highlight: false });
        result.push({ text: match, highlight: true });
        last = offset + match.length;
      });
      if (last < text.length) result.push({ text: text.slice(last), highlight: false });
      return result;
    });
    return () => hFn('span', parts.value.map(p =>
      p.highlight ? hFn('mark', p.text) : p.text
    ));
  },
});

import App from './App.vue';
import { use } from 'echarts/core'
import { BarChart } from 'echarts/charts'
import { TooltipComponent } from 'echarts/components'
import { CanvasRenderer } from 'echarts/renderers'
import ECharts from 'vue-echarts'

use([CanvasRenderer, BarChart, TooltipComponent])
// import VueSweetalert2 from 'vue-sweetalert2';
import Swal from 'sweetalert2';  // ? Use Swal directly
import 'sweetalert2/dist/sweetalert2.min.css';
import { FsmStart } from './store/modules/fsm/messages';
import { post } from './store/modules/fsm/index';
import VueSimpleContextMenu from 'vue-simple-context-menu';
import 'vue-simple-context-menu/dist/vue-simple-context-menu.css';
import PrimeVue from 'primevue/config';
import PickList from 'primevue/picklist';
import Tree from 'primevue/tree';
import Accordion from 'primevue/accordion';
import AccordionTab from 'primevue/accordiontab'
import Editor from 'primevue/editor'
import AutoComplete from 'primevue/autocomplete'
import Aura from '@primevue/themes/aura';
import 'primeicons/primeicons.css';
import CurrentNurseBio from '@/components/private-layouts/nurses/CurrentNurseBio'
import VCalendar from 'v-calendar';
import 'v-calendar/style.css';
import 'quill/dist/quill.snow.css'
import { useAllergyStore } from './stores/allergyStore'
import Datepicker from '@vuepic/vue-datepicker'
import '@vuepic/vue-datepicker/dist/main.css'


import 'primeicons/primeicons.css';
import DataTable from 'datatables.net-dt';
import axios from 'axios';
import { installRequestMarker } from '@/api/requestMarker';
import { installMfaRequiredHandler } from '@/api/mfaRequired';
import { installStoredFileLinks } from '@/api2/fileTickets';

// Every request we make to our own backends carries the marker their
// cross-site guards look for. Installed before the app is created, so no
// request can be issued without it.
installRequestMarker();

// A 401 mfa_required sends the user to sign in again (a fresh second factor).
// Only axios calls can be intercepted: images, iframes and PDF.js fetches of a
// stored file fall back to "Document unavailable" instead.
installMfaRequiredHandler(axios, { store, router });

// Main's components open stored files by URL (anchors, window.open), which
// cannot carry the bearer token: each such opening gets a single-use content
// ticket URL from the API instead (decision D5, api2/fileTickets.js).
installStoredFileLinks();

// ? Create Vue App
const app = createApp(App);
const pinia = createPinia(); // Vue 3's state management alternative to Vuex

// ? Global Event Bus (Vue 3 way) - Create a proper event bus
const eventBus = {
  events: {},
  $on(event, callback) {
    if (!this.events[event]) {
      this.events[event] = [];
    }
    this.events[event].push(callback);
  },
  $off(event, callback) {
    if (!this.events[event]) return;
    if (callback) {
      this.events[event] = this.events[event].filter(cb => cb !== callback);
    } else {
      delete this.events[event];
    }
  },
  $emit(event, ...args) {
    if (!this.events[event]) return;
    this.events[event].forEach(callback => {
      callback(...args);
    });
  }
};

app.config.globalProperties.$vueEventBus = eventBus;
app.config.globalProperties.$sessionTimer = null;
app.config.globalProperties.$gotInitialMessage = () => app.config.globalProperties.$gotInitialMessage ? true : false;
app.config.globalProperties.$swal = (options = {}) => {
  // Convert old 'type' property to 'icon' for SweetAlert2 compatibility
  if (options && options.type && !options.icon) {
    options.icon = options.type;
    delete options.type;
  }
  // Keep alerts/toasts above app modals. Some modals (e.g. the teleported
  // add-user form) sit at very high z-index values, and SweetAlert2's default
  // container z-index (1060) would otherwise render the toast underneath them.
  const userDidOpen = options.didOpen;
  return Swal.fire({
    ...options,
    didOpen: (popup) => {
      const container = Swal.getContainer();
      if (container) container.style.zIndex = '2147483647';
      if (typeof userDidOpen === 'function') userDidOpen(popup);
    },
  });
};

// ? Register Plugins
app.use(router);
app.use(store); // Vuex
window.store = store
app.use(pinia); // Optional: If you plan to switch to Pinia later
app.use(LoadingPlugin);

// Keep allergy startup independent so it can't block privilege/bootstrap flow.
try {
  const allergyStore = useAllergyStore(pinia);
  if (allergyStore && typeof allergyStore.getbackendAllergies === "function") {
    allergyStore.getbackendAllergies();
  }
} catch (e) {
  console.warn('Allergy store startup failed:', e);
}

store.subscribe((mutation) => {
  if (mutation.type === 'login/LoggedIn') {
    syncPrivilegesAfterSessionChange();
  }
  if (mutation.type === 'login/LoggedOut') {
    clearPrivilegesStore();
    clearOrganizationStore();
  }
});

// app.use(VueSweetalert2);
// app.use(PrimeVue);
app.use(PrimeVue, {
  theme: {
    preset: Aura,
    options: {
      prefix: 'p',
      darkModeSelector: false,
      cssLayer: false
    }
  }
});

// ? Register Components
app.use(VCalendar, {})
// app.component('v-calendar', Calendar);
app.component('text-highlight', TextHighlight);
app.component('font-awesome-icon', FontAwesomeIcon);
app.component('v-chart', ECharts);
app.component('vue-simple-context-menu', VueSimpleContextMenu);
app.component('current-nurse-bio', CurrentNurseBio);
app.component('PickList', PickList);
app.component('Tree', Tree);
app.component('AccordionComponent', Accordion);
app.component('AccordionTab', AccordionTab);
app.component('EditorComponent', Editor);
app.component('AutoComplete', AutoComplete);
app.component('Datepicker', Datepicker)

// ? FontAwesome Icons
// library.add(faCaretDown, faBars, faCog, faSearch, faCalendarAlt,
//   faCaretRight, faUserMd, faPlaneDeparture, faFile, faCalendarCheck,
//   faFilter, faSortAmountUp, faPlusCircle, faMinusCircle, faTimesCircle,
//   faCheckSquare, faTimes, faCheck, faSave, faEye, faTrash, faCrop, faQuestionCircle,
//   faSearchPlus, faFilePdf, faFileMedical, faFileMedicalAlt, faFileAlt, faMapMarkerAlt,
//   faPhone, faEnvelope, faBuilding, faFax, faArrowRight, faAngleDoubleLeft, faPrescriptionBottle,
//   faAngleDoubleRight, faAngleLeft, faAngleRight, faInbox, faUserMd, faNotesMedical, faThumbtack,
//   faPen, faBan, faStar, faAddressBook, faPencilAlt, faSpinner, faComment, faCommentDots, faCommentAlt, faDollarSign);
library.add(faCaretDown, faBars, faCog, faSearch, faCalendarAlt,
  faCaretRight, faUser, faUserMd, faUserNurse, faUsers, faPlaneDeparture, faFile, faCalendarCheck,
  faFilter, faSortAmountUp, faPlusCircle, faMinusCircle, faTimesCircle,
  faCheckSquare, faTimes, faCheck, faSave, faEye, faTrash, faCrop, faQuestionCircle,
  faSearchPlus, faFilePdf, faFileMedical, faFileMedicalAlt, faFileAlt, faHome, faMapMarkedAlt, faMapMarkerAlt,
  faPhone, faEnvelope, faBuilding, faFax, faArrowRight, faAngleDoubleLeft, faPrescriptionBottle,
  faAngleDoubleRight, faAngleLeft, faAngleRight, faInbox, faNotesMedical, faThumbtack,
  faPen, faBan, faStar, faAddressBook, faPencilAlt, faSpinner, faComment, faCommentDots, faCommentAlt, faDollarSign, faChevronUp, faChevronDown, faChevronRight, faReply, faBell, faClipboardCheck, faUserPlus, faFileVideo, faPlay, faImages);
// The FSM's start message is deliberately not dispatched.
//
// `FsmStart` drove the session lifecycle: it decided whether the user was
// logged in and navigated to the login page if not. That is the identity
// provider's job now, and the machine actively fought the router — it
// navigated to `/login` on every boot, including the boot that was in the
// middle of completing an OIDC redirect.
//
// The feature machine-states (pdf, cda, reports) are still registered and still
// respond to their own messages; only the session lifecycle is retired. The
// whole `store/modules/fsm/` tree goes when those features move to native
// clients — see port/design/08-frontend-migration.md §6.3.
// post({ message: FsmStart });

// ? Mount App
console.log('About to mount Vue app...');
console.log('Window width:', window.innerWidth);
console.log('User agent:', navigator.userAgent);
app.mount('#app');
nextTick(() => {
  syncPrivilegesAfterSessionChange();
});
console.log('Vue app mounted successfully!');